import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, ChevronRight, Share2, Bookmark, FileText, Download, Upload, CalendarDays } from 'lucide-react';
import { DAILY_DOSAGES, HERO_IMAGES } from '../mock';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { toast } from '../hooks/use-toast';
import DosageUploadDialog from '../components/DosageUploadDialog';
import api, { fileUrl } from '../lib/api';

export default function Dosage() {
  const [uploaded, setUploaded] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);

  const loadDosages = useCallback(() => {
    api.listDosages().then(list => {
      const todayISO = new Date().toISOString().slice(0, 10);
      const fmt = (iso) => {
        if (!iso) return '';
        const d = new Date(`${iso}T00:00:00`);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
      };
      const mapped = list.map((u) => {
        const iso = u.post_date || (u.created_at ? u.created_at.slice(0, 10) : '');
        const isToday = iso === todayISO;
        return {
          id: u.id,
          postDate: iso,
          date: isToday ? 'Today' : fmt(iso),
          title: u.title,
          verseRef: u.verse_ref || '',
          verse: u.verse || '',
          body: u.body,
          author: u.author || 'Pastor Oladipo Babalola',
          minutes: u.minutes || 3,
          image: u.image_url ? fileUrl(u.image_url) : HERO_IMAGES.bibleSunrise,
        };
      });
      setUploaded(mapped);
    }).catch(() => setUploaded([]));
  }, []);

  useEffect(() => {
    loadDosages();
    api.listMaterials().then(setMaterials).catch(() => setMaterials([]));
  }, [loadDosages]);

  const merged = useMemo(() => [...uploaded, ...DAILY_DOSAGES], [uploaded]);
  const [selected, setSelected] = useState(null);
  useEffect(() => { if (merged.length && !selected) setSelected(merged[0]); }, [merged, selected]);

  if (!selected) return null;

  const onSave = () => {
    try {
      const saved = JSON.parse(localStorage.getItem('dogm_saved_dosages') || '[]');
      if (!saved.includes(selected.id)) {
        saved.push(selected.id);
        localStorage.setItem('dogm_saved_dosages', JSON.stringify(saved));
      }
    } catch (_) {}
    toast({ title: 'Saved', description: 'This dosage is now in your library.' });
  };

  const onShare = () => {
    if (navigator.share) {
      navigator.share({ title: selected.title, text: selected.verse }).catch(() => {});
    } else {
      toast({ title: 'Link copied', description: 'Share this dosage with a friend.' });
    }
  };

  return (
    <div>
      <section className="relative pt-32 pb-14 md:pt-40 md:pb-20">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES.bibleSunrise2} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B27]/95 via-[#0B1B27]/75 to-[#0B1B27]/40" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 text-white">
          <p className="text-xs tracking-[0.35em] uppercase text-gold-brand mb-4">Daily Devotional</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h1 className="font-serif-display text-5xl md:text-7xl leading-tight">Today&rsquo;s Dosage</h1>
              <p className="mt-6 text-lg md:text-xl text-white/80 max-w-2xl">
                A short, Scripture-rooted word to steady your soul. Take it slowly. Let it work.
              </p>
            </div>
            <Button onClick={() => setUploadOpen(true)} className="bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold h-11 px-5 self-start md:self-end whitespace-nowrap">
              <Upload className="h-4 w-4 mr-2" /> Publish Dosage
            </Button>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid lg:grid-cols-3 gap-10">
          <article className="lg:col-span-2">
            <div className="relative aspect-[16/8] rounded-2xl overflow-hidden shadow-lg mb-8">
              <img src={selected.image} alt={selected.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5">
                <Badge className="bg-gold-brand text-ink font-semibold">{selected.date}</Badge>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-ink/60 mb-3">
              <span data-testid="dosage-detail-date" className="flex items-center gap-1.5 font-semibold text-teal-deep">
                <CalendarDays className="h-3.5 w-3.5" /> {selected.date}
              </span>
              <span>&middot;</span>
              <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {selected.minutes} min read</span>
              {selected.verseRef && <>
                <span>&middot;</span>
                <span className="text-gold-deep font-medium">{selected.verseRef}</span>
              </>}
              <span>&middot;</span>
              <span>By {selected.author}</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight">{selected.title}</h2>
            {selected.verse && (
              <blockquote className="mt-6 font-serif-display italic text-2xl text-teal-deep border-l-4 border-gold-brand pl-6 leading-snug">
                &ldquo;{selected.verse}&rdquo;
                {selected.verseRef && <cite className="block text-sm not-italic text-ink/50 mt-3 font-sans">&mdash; {selected.verseRef}</cite>}
              </blockquote>
            )}
            <div className="mt-6 space-y-4 text-lg leading-relaxed text-ink/80 whitespace-pre-line">
              <p>{selected.body}</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={onSave} className="bg-teal-brand hover:bg-teal-deep text-white h-11 px-5">
                <Bookmark className="mr-2 h-4 w-4" /> Save
              </Button>
              <Button variant="outline" onClick={onShare} className="border-teal-brand text-teal-deep hover:bg-teal-brand hover:text-white h-11 px-5">
                <Share2 className="mr-2 h-4 w-4" /> Share
              </Button>
              <Link to="/prayer"><Button variant="outline" className="h-11 px-5 border-gold-brand text-gold-deep hover:bg-gold-brand hover:text-ink">Request Prayer</Button></Link>
            </div>
          </article>

          <aside className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CalendarDays className="h-5 w-5 text-teal-deep" />
                <h3 className="font-serif-display text-2xl text-ink">Calendar</h3>
              </div>
              <div data-testid="dosage-calendar" className="rounded-2xl bg-white border border-ink/10 p-2">
                <Calendar
                  mode="single"
                  selected={selected?.postDate ? new Date(`${selected.postDate}T00:00:00`) : undefined}
                  onSelect={(d) => {
                    if (!d) return;
                    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                    const match = merged.find(x => x.postDate === iso);
                    if (match) {
                      setSelected(match);
                      window.scrollTo({ top: 400, behavior: 'smooth' });
                    } else {
                      toast({ title: 'No dosage on that day', description: 'Try picking a highlighted date.' });
                    }
                  }}
                  modifiers={{
                    hasDosage: merged.filter(x => x.postDate).map(x => new Date(`${x.postDate}T00:00:00`)),
                  }}
                  modifiersClassNames={{
                    hasDosage: 'relative font-semibold text-teal-deep after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-gold-brand',
                  }}
                  className="mx-auto"
                />
              </div>
              <p className="text-xs text-ink/50 mt-2 text-center">Dates with a gold dot have a dosage &mdash; tap to open.</p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="h-5 w-5 text-teal-deep" />
                <h3 className="font-serif-display text-2xl text-ink">Archive</h3>
              </div>
              <div className="space-y-3">
                {merged.map(d => (
                  <button key={d.id} onClick={() => { setSelected(d); window.scrollTo({ top: 400, behavior: 'smooth' }); }}
                    className={`w-full text-left group flex gap-3 p-3 rounded-xl border transition-all ${selected.id === d.id ? 'bg-cream border-gold-brand' : 'bg-white border-ink/10 hover:border-teal-brand'}`}>
                    <img src={d.image} alt={d.title} className="h-16 w-16 rounded-lg object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs tracking-[0.2em] uppercase text-gold-deep mb-1">{d.date}</p>
                      <p className="font-serif-display text-base leading-tight text-ink group-hover:text-teal-deep">{d.title}</p>
                      {d.verseRef && <p className="text-xs text-ink/50 mt-1">{d.verseRef} &middot; {d.minutes} min</p>}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {materials.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="h-5 w-5 text-teal-deep" />
                  <h3 className="font-serif-display text-2xl text-ink">Study Materials</h3>
                </div>
                <div className="space-y-3">
                  {materials.slice(0, 6).map(m => (
                    <a key={m.id} href={fileUrl(m.file_url)} target="_blank" rel="noreferrer" className="flex gap-3 p-3 rounded-xl bg-white border border-ink/10 hover:border-teal-brand transition-colors">
                      <div className="h-12 w-12 rounded-lg bg-cream flex items-center justify-center flex-shrink-0"><FileText className="h-5 w-5 text-teal-deep" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="font-serif-display text-base text-ink truncate">{m.title}</p>
                        <p className="text-xs text-ink/50">{m.file_name}</p>
                      </div>
                      <Download className="h-4 w-4 text-ink/40 self-center" />
                    </a>
                  ))}
                  <Link to="/materials" className="block text-center text-sm text-teal-deep hover:underline pt-1">View all materials \u2192</Link>
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>

      <section className="py-14 bg-[#128C7E] text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 flex flex-col md:flex-row items-center gap-6 md:gap-10">
          <div className="h-16 w-16 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <BookOpen className="h-8 w-8 text-white" strokeWidth={1.5} />
          </div>
          <div className="flex-1 text-center md:text-left">
            <p className="text-xs tracking-[0.3em] uppercase text-white/70 mb-2">Dosage For Today</p>
            <h3 className="font-serif-display text-2xl md:text-3xl">A fresh dose of grace, every morning</h3>
            <p className="mt-2 text-white/85">Return here each day for a Scripture-rooted devotional to steady your soul.</p>
          </div>
          <Button
            data-testid="scroll-to-today-btn"
            onClick={() => { setSelected(merged[0]); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="bg-white text-[#128C7E] hover:bg-white/90 font-semibold h-11 px-6"
          >
            <BookOpen className="mr-2 h-4 w-4" /> Read Today&rsquo;s Dosage
          </Button>
        </div>
      </section>

      <DosageUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={loadDosages} />
    </div>
  );
}
